import { Component, OnInit, inject, effect, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { QualityService } from '../../core/services/quality.service';
import { SessionService } from '../../core/session.service';
import { API_BASE_URL } from '../../core/api-base-url.token';
import type { HttpResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

interface Rapport {
  mois: string;
  periode: string;
  fichier: string;
  dernier?: boolean;
  selectionne?: boolean;
  dateGeneration?: string;
}

@Component({
  selector: 'app-rapport',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './rapport.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./rapport.component.scss']
})
export class RapportComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly qualityService = inject(QualityService);
  private readonly session = inject(SessionService);
  private readonly baseRoot = inject(API_BASE_URL).replace(/\/+$/, '');
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  
  annee = new Date().getFullYear();
  rapports: Rapport[] = [];
  anneesOuvertes: { [key: number]: boolean } = {};
  downloading = false;
  refreshing = false;

  constructor() {
    effect(() => {
      const est = this.session.selectedId();
      if (est && !this.refreshing) {
        this.loadDocuments(est);
      } else if (!est) {
        this.rapports = [];
      }
    });
  }

  ngOnInit(): void {
    // L'effect se charge du chargement initial
  }

  private loadDocuments(establishmentId: string): void {
    if (!establishmentId?.trim()) return;
    
    const url = `${this.baseRoot}/api/v1/quality/documents/${encodeURIComponent(establishmentId)}`;
    this.http.get<any[]>(url)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (documents) => {
          this.rapports = documents.map((doc, index) => {
            console.log('Document:', doc);
            return {
              mois: this.formatMonth(doc.period_month),
              periode: this.formatPeriod(doc.period_month),
              fichier: doc.reference_code + '.pdf',
              dernier: index === 0, // Le premier est le plus récent
              dateGeneration: doc.generated_at ? this.formatDate(doc.generated_at) : undefined
            };
          });
          
          // Déplier l'année la plus récente
          const annees = this.getAnnees();
          if (annees.length > 0) {
            this.anneesOuvertes[annees[0]] = true;
          }
        },
        error: () => console.error('Erreur chargement documents')
      });
  }

  private formatMonth(periodMonth: string): string {
    if (!periodMonth) return this.translate.instant('rapport.invalidDate');
    const date = new Date(periodMonth);
    if (isNaN(date.getTime())) return this.translate.instant('rapport.invalidDate');

    const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  }

  /**
   * Libellé affiché du mois (traduit) — construit à partir de `mois` (toujours stocké en
   * français, voir formatMonth) sans jamais modifier la valeur elle-même : telecharger()/
   * telechargerSingle()/getRapportsParAnnee() reparsent cette valeur pour retrouver
   * l'année/le mois, donc elle doit rester intacte en français.
   */
  getMonthLabel(mois: string | undefined): string {
    if (!mois) return '';
    const parts = mois.split(' ');
    if (parts.length !== 2) return mois;
    const [moisFr, annee] = parts;
    const monthsFr = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    const idx = monthsFr.indexOf(moisFr.toLowerCase());
    if (idx === -1) return mois;
    const monthsTranslated: string[] = this.translate.instant('rapport.months');
    return `${monthsTranslated[idx]} ${annee}`;
  }

  private formatPeriod(periodMonth: string): string {
    if (!periodMonth) return this.translate.instant('rapport.invalidPeriod');
    const date = new Date(periodMonth);
    if (isNaN(date.getTime())) return this.translate.instant('rapport.invalidPeriod');

    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const lastDay = new Date(year, month, 0).getDate();
    return this.translate.instant('rapport.periodRange', {
      startDay: `01/${month.toString().padStart(2, '0')}/${year}`,
      endDay: `${lastDay}/${month.toString().padStart(2, '0')}/${year}`,
    });
  }

  private formatDate(dateString: string): string {
    console.log('Formatting date:', dateString);
    // Corriger le format de date en ajoutant :00 à la fin si manquant
    let correctedDate = dateString;
    if (dateString.endsWith('+00')) {
      correctedDate = dateString + ':00';
    }
    const date = new Date(correctedDate);
    if (isNaN(date.getTime())) {
      console.log('Invalid date');
      return '';
    }
    const formatted = date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    console.log('Formatted date:', formatted);
    return formatted;
  }

  toggleSelection(rapport: Rapport) {
    rapport.selectionne = !rapport.selectionne;
  }

  telecharger(rapport: Rapport) {
    if (this.downloading) return;
    
    const est = this.session.selectedId();
    if (!est) {
      console.warn('Aucun établissement sélectionné');
      return;
    }

    // Extraire l'année et le mois du rapport
    const parts = rapport.mois.split(' ');
    if (parts.length !== 2) return;
    
    const [mois, annee] = parts;
    const year = parseInt(annee);
    if (isNaN(year)) return;
    
    const monthNames = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 
                       'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    const month = monthNames.indexOf(mois.toLowerCase()) + 1;

    if (month === 0) return;

    this.downloading = true;
    this.qualityService.downloadMonthlyRaqPdf(est, year, month, false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (resp: HttpResponse<Blob>) => {
        if (!resp.body) return;
        const blob = resp.body;
        const cd = resp.headers.get('Content-Disposition') || '';
        const match = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(cd);
        const suggested = match ? decodeURIComponent(match[1]) : `Rapport-ISO-${year}-${String(month).padStart(2, '0')}.pdf`;
        this.saveBlob(blob, suggested);
        this.downloading = false;
      },
      error: () => {
        this.downloading = false;
      }
    });
  }

  async telechargerSelection() {
    if (this.downloading) return;
    
    const selection = this.rapports.filter(r => r.selectionne);
    if (selection.length === 0) return;
    
    this.downloading = true;
    
    // Téléchargement séquentiel pour éviter la surcharge
    for (const rapport of selection) {
      await this.telechargerSingle(rapport);
    }
    
    this.downloading = false;
  }
  
  private telechargerSingle(rapport: Rapport): Promise<void> {
    return new Promise((resolve) => {
      const est = this.session.selectedId();
      if (!est) {
        resolve();
        return;
      }

      const parts = rapport.mois.split(' ');
      if (parts.length !== 2) {
        resolve();
        return;
      }
      
      const [mois, annee] = parts;
      const year = parseInt(annee);
      if (isNaN(year)) {
        resolve();
        return;
      }
      
      const monthNames = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 
                         'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
      const month = monthNames.indexOf(mois.toLowerCase()) + 1;

      if (month === 0) {
        resolve();
        return;
      }

      this.qualityService.downloadMonthlyRaqPdf(est, year, month, false)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (resp: HttpResponse<Blob>) => {
            if (!resp.body) {
              resolve();
              return;
            }
            const blob = resp.body;
            const cd = resp.headers.get('Content-Disposition') || '';
            const match = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(cd);
            const suggested = match ? decodeURIComponent(match[1]) : `Rapport-ISO-${year}-${String(month).padStart(2, '0')}.pdf`;
            this.saveBlob(blob, suggested);
            resolve();
          },
          error: () => {
            resolve();
          }
        });
    });
  }

  getDernierRapport(): Rapport | undefined {
    return this.rapports.find(r => r.dernier);
  }

  hasSelection(): boolean {
    return this.rapports.some(r => r.selectionne);
  }

  getSelectionCount(): number {
    return this.rapports.filter(r => r.selectionne).length;
  }

  getRapportsParAnnee(): { [key: number]: Rapport[] } {
    return this.rapports.reduce((acc, rapport) => {
      const annee = parseInt(rapport.mois.split(' ')[1]);
      if (!acc[annee]) acc[annee] = [];
      acc[annee].push(rapport);
      return acc;
    }, {} as { [key: number]: Rapport[] });
  }

  getAnnees(): number[] {
    return Object.keys(this.getRapportsParAnnee()).map(Number).sort((a, b) => b - a);
  }

  toggleAnnee(annee: number): void {
    this.anneesOuvertes[annee] = !this.anneesOuvertes[annee];
  }

  isAnneeOuverte(annee: number): boolean {
    return this.anneesOuvertes[annee] || false;
  }

  refreshRapports() {
    const est = this.session.selectedId();
    if (!est || this.refreshing) return;
    
    console.log('Début refresh rapports');
    this.refreshing = true;
    
    // Générer le rapport du mois en cours
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    
    console.log(`Génération rapport: ${est}, ${year}-${month}`);
    
    this.qualityService.generateMonthlyReport(est, year, month)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          console.log('Génération réussie:', response);
          this.refreshing = false;
          this.loadDocuments(est);
        },
        error: (err) => {
          console.error('Erreur génération:', err);
          this.refreshing = false;
        }
      });
  }

  private saveBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    
    // Délai pour assurer le téléchargement
    setTimeout(() => {
      a.remove();
      URL.revokeObjectURL(url);
    }, 100);
  }
}
